// client/src/services/versionManagement.ts
// Manage app versions, deployments, and rollbacks

export interface VersionInfo {
  current: string;
  previous: string;
  canary: string | null;
  deployedAt: number;
  deployedBy: string;
  status: 'stable' | 'canary' | 'rolling' | 'rollback';
}

export interface DeploymentMetrics {
  version: string;
  errorRate: number;
  affectedUsers: number;
  criticalChunks: number;
  shouldRollback: boolean;
  rollbackReason?: string;
}

class VersionManager {
  private currentVersion: string;
  private previousVersion: string;
  private canaryVersion: string | null = null;
  private deploymentMetrics = new Map<string, DeploymentMetrics>();
  private initialized = false;

  constructor() {
    this.currentVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
    this.previousVersion = localStorage.getItem('app_previous_version') || this.currentVersion;
  }

  initialize() {
    if (this.initialized) return;
    this.initialized = true;  }

  getCurrentVersion(): string {
    return this.currentVersion;
  }

  getPreviousVersion(): string {
    return this.previousVersion;
  }

  getVersionInfo(): VersionInfo {
    return {
      current: this.currentVersion,
      previous: this.previousVersion,
      canary: this.canaryVersion,
      deployedAt: parseInt(localStorage.getItem('app_deployed_at') || '0', 10),
      deployedBy: localStorage.getItem('app_deployed_by') || 'unknown',
      status: this.getDeploymentStatus(),
    };
  }

  recordDeployment(version: string, deployedBy: string = 'system') {
    this.previousVersion = this.currentVersion;
    this.currentVersion = version;

    localStorage.setItem('app_previous_version', this.previousVersion);
    localStorage.setItem('app_current_version', version);
    localStorage.setItem('app_deployed_at', Date.now().toString());
    localStorage.setItem('app_deployed_by', deployedBy);  }

  recordCanaryDeployment(version: string, percentage: number) {
    this.canaryVersion = version;
    localStorage.setItem('app_canary_version', version);
    localStorage.setItem('app_canary_percentage', percentage.toString());  }

  recordDeploymentMetrics(version: string, metrics: DeploymentMetrics) {
    this.deploymentMetrics.set(version, metrics);

    // Check if rollback is needed
    if (metrics.shouldRollback) {
      console.error(`[VersionManager] Rollback triggered for ${version}: ${metrics.rollbackReason}`);
      this.triggerRollback(version);
    }
  }

  getDeploymentMetrics(version: string): DeploymentMetrics | undefined {
    return this.deploymentMetrics.get(version);
  }

  private triggerRollback(version: string) {
    const metrics = this.deploymentMetrics.get(version);
    if (!metrics) return;

    console.warn(`[VersionManager] Rolling back from ${version} to ${this.previousVersion}`);

    // Record rollback event
    localStorage.setItem('app_rollback_from', version);
    localStorage.setItem('app_rollback_at', Date.now().toString());
    localStorage.setItem('app_rollback_reason', metrics.rollbackReason || 'unknown');

    // Clear service worker cache to force fresh load
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(reg => reg.unregister());
      });
    }

    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    // Reload with previous version
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }

  private getDeploymentStatus(): 'stable' | 'canary' | 'rolling' | 'rollback' {
    if (localStorage.getItem('app_rollback_from')) return 'rollback';
    if (this.canaryVersion) return 'canary';
    if (localStorage.getItem('app_deployment_status') === 'rolling') return 'rolling';
    return 'stable';
  }

  checkForUpdates(): Promise<boolean> {
    return fetch('/api/version/check')
      .then(res => res.json())
      .then(data => {
        if (data.version && data.version !== this.currentVersion) {          return true;
        }
        return false;
      })
      .catch(error => {
        console.error('[VersionManager] Failed to check for updates:', error);
        return false;
      });
  }
}

export const versionManager = new VersionManager();

